import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ContentRenderer from './ContentRenderer.jsx';
import LearnMode from './LearnMode.jsx';
import VoiceAnswerMode from './VoiceAnswerMode.jsx';
import FolderChatSidebar from './FolderChatSidebar.jsx';
import ShareFolderModal from './ShareFolderModal.jsx';
import CardImage from './CardImage.jsx';
import { trackEvent, authApi } from '../firebase/config.js';
import { recordStudySession } from '../firebase/progress.js';
import { Events } from '../firebase/events.js';
import { applyRating } from '../utils/srs.js';
import { ENV } from '../config/env.js';

const API_BASE_URL = ENV.apiBaseUrl;

const ExplainButton = ({ card }) => {
    const [state, setState] = useState('idle');
    const [explanation, setExplanation] = useState('');

    const handleExplain = async (e) => {
        e.stopPropagation();
        setState('loading');
        try {
            const data = await authApi(`${API_BASE_URL}/api/explain-card`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ question: card.question, answer: card.answer }),
            });
            setExplanation(data.explanation || '');
            setState('done');
        } catch {
            setState('error');
        }
    };

    if (state === 'idle') return (
        <button
            onClick={handleExplain}
            className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-teal-400/25 px-3 py-1 text-xs text-teal-300 hover:bg-teal-500/10 transition"
        >
            <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Explain this
        </button>
    );
    if (state === 'loading') return (
        <p className="mt-2 text-xs text-slate-500 animate-pulse">Getting explanation…</p>
    );
    if (state === 'error') return (
        <p className="mt-2 text-xs text-rose-400">Couldn't load explanation.</p>
    );
    return (
        <div className="mt-3 rounded-xl bg-teal-500/8 border border-teal-400/15 p-3 text-xs text-slate-300 leading-relaxed">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-teal-400/70 mb-1.5">Explanation</p>
            {explanation}
        </div>
    );
};

const CONFIDENCE_OPTIONS = [
    { key: 'again', label: 'Again', color: 'border border-rose-500/40 text-rose-300 hover:bg-rose-500/10 active:bg-rose-500/20' },
    { key: 'hard',  label: 'Hard',  color: 'border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 active:bg-amber-500/20' },
    { key: 'good',  label: 'Good',  color: 'border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 active:bg-emerald-500/20' },
    { key: 'easy',  label: 'Easy',  color: 'border border-sky-500/40 text-sky-300 hover:bg-sky-500/10 active:bg-sky-500/20' },
];

const CONFIDENCE_BADGE = {
    again: 'bg-rose-500/15 text-rose-300',
    hard:  'bg-amber-500/15 text-amber-300',
    good:  'bg-emerald-500/15 text-emerald-300',
    easy:  'bg-sky-500/15 text-sky-300',
};

const FlashcardViewer = ({ folder, onClose, onLaunchGame, onLaunchAnamnesisNemesis, entitlements, isDevMode, onOpenCredits, onCardRated, onLaunchExam }) => {
    const [deck, setDeck] = useState([...folder.cards]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isFlipped, setIsFlipped] = useState(false);
    const [isArrangeMode, setIsArrangeMode] = useState(false);
    const [reviewMode, setReviewMode] = useState('all');
    const [isReading, setIsReading] = useState(false);
    const [speechRate, setSpeechRate] = useState(1);
    const [speechDelay, setSpeechDelay] = useState(3);
    const [voices, setVoices] = useState([]);
    const [selectedVoice, setSelectedVoice] = useState('');
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [confidence, setConfidence] = useState({});
    const [showLearn, setShowLearn] = useState(false);
    const [showVoice, setShowVoice] = useState(false);
    const [showChat, setShowChat] = useState(false);
    const [showShare, setShowShare] = useState(false);
    const speechTimeoutRef = useRef(null);
    const touchStartX = useRef(0);
    const touchStartY = useRef(0);
    const handlersRef = useRef({});

    const studyDeck = useMemo(() => {
        if (reviewMode === 'flagged') return deck.filter(card => card.isFlagged);
        if (reviewMode === 'starred') return deck.filter(card => card.isStarred);
        return deck;
    }, [deck, reviewMode]);

    const currentCard = studyDeck[currentIndex];
    const currentCardId = currentCard ? currentCard.id : null;
    const flaggedCount = useMemo(() => deck.filter(c => c.isFlagged).length, [deck]);
    const starredCount = useMemo(() => deck.filter(c => c.isStarred).length, [deck]);

    const knownCount = useMemo(
        () => studyDeck.filter(c => confidence[c.id] === 'good' || confidence[c.id] === 'easy').length,
        [studyDeck, confidence]
    );
    const progressPct = studyDeck.length > 0 ? (knownCount / studyDeck.length) * 100 : 0;
    const allRated = studyDeck.length > 0 && studyDeck.every(c => confidence[c.id]);
    const againCount = studyDeck.filter(c => confidence[c.id] === 'again').length;
    const hardCount  = studyDeck.filter(c => confidence[c.id] === 'hard').length;

    // Track lastViewed
    useEffect(() => {
        if (isArrangeMode || !currentCardId) return;
        setDeck(prevDeck => {
            const cardInDeck = prevDeck.find(c => c.id === currentCardId);
            if (cardInDeck && (!cardInDeck.lastViewed || (Date.now() - cardInDeck.lastViewed > 5000))) {
                return prevDeck.map(card => card.id === currentCardId ? { ...card, lastViewed: Date.now() } : card);
            }
            return prevDeck;
        });
    }, [currentCardId, isArrangeMode]);

    // Study start analytics
    useEffect(() => {
        trackEvent(Events.study_start, { folderId: folder.id, folderName: folder.name, deckSize: deck.length });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Load TTS voices
    useEffect(() => {
        const loadVoices = () => {
            const availableVoices = window.speechSynthesis.getVoices();
            const englishVoices = availableVoices.filter(voice => voice.lang.startsWith('en'));
            setVoices(englishVoices);
            if (englishVoices.length > 0 && !selectedVoice) {
                setSelectedVoice(englishVoices[0].name);
            }
        };
        window.speechSynthesis.onvoiceschanged = loadVoices;
        loadVoices();
        return () => { window.speechSynthesis.onvoiceschanged = null; };
    }, [selectedVoice]);

    const speak = useCallback((text, onEnd) => {
        const normalized = typeof text === 'string'
            ? text
            : Array.isArray(text)
                ? text.join(', ')
                : (text != null ? JSON.stringify(text) : '');
        const utterance = new SpeechSynthesisUtterance(normalized);
        const voice = voices.find(v => v.name === selectedVoice);
        if (voice) utterance.voice = voice;
        utterance.rate = speechRate;
        utterance.onend = () => { if (isReading && typeof onEnd === 'function') onEnd(); };
        utterance.onerror = () => { try { if (typeof onEnd === 'function') onEnd(); } catch {} };
        try { if (window.speechSynthesis.speaking) window.speechSynthesis.cancel(); } catch {}
        try { window.speechSynthesis.cancel(); } catch {}
        setTimeout(() => {
            if (!isReading) return;
            try { window.speechSynthesis.speak(utterance); } catch {}
        }, 80);
    }, [voices, selectedVoice, speechRate, isReading]);

    const stopReading = useCallback(() => {
        setIsReading(false);
        window.speechSynthesis.cancel();
        if (speechTimeoutRef.current) clearTimeout(speechTimeoutRef.current);
    }, []);

    // TTS auto-play sequence
    useEffect(() => {
        if (!isReading || !currentCard) return;
        const readCardSequence = () => {
            setIsFlipped(false);
            speak(`Question: ${currentCard.question}`, () => {
                speechTimeoutRef.current = setTimeout(() => {
                    if (!isReading) return;
                    setIsFlipped(true);
                    speak(`Answer: ${currentCard.answer}`, () => {
                        if (!isReading) return;
                        setCurrentIndex(prev => (prev + 1) % studyDeck.length);
                    });
                }, speechDelay * 1000);
            });
        };
        readCardSequence();
        return () => {
            clearTimeout(speechTimeoutRef.current);
            try { window.speechSynthesis.cancel(); } catch {}
        };
    }, [isReading, currentIndex, studyDeck, speechDelay, currentCard, speak]);

    // Keyboard navigation
    useEffect(() => {
        const onKey = (e) => {
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') return;
            const h = handlersRef.current;
            if (e.key === 'ArrowRight') h.goToNext();
            else if (e.key === 'ArrowLeft') h.goToPrev();
            else if (e.key === ' ') { e.preventDefault(); h.handleCardClick(); }
            else if (e.key === 'Escape') h.closeSelf();
            else if (e.key === 's' || e.key === 'S') h.toggleStarCurrent();
            else if (h.isFlipped && h.currentCard) {
                const KEYS = { '1': 'again', '2': 'hard', '3': 'good', '4': 'easy' };
                if (KEYS[e.key]) h.handleConfidence(KEYS[e.key]);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleCardClick = () => {
        if (studyDeck.length === 0) return;
        stopReading();
        setIsFlipped(prev => !prev);
        if (currentCardId) trackEvent(Events.card_flip, { folderId: folder.id, cardId: currentCardId, flippedTo: !isFlipped ? 'back' : 'front' });
    };

    const goToNext = useCallback(() => {
        if (studyDeck.length === 0) return;
        stopReading();
        setIsFlipped(false);
        setCurrentIndex(prev => (prev + 1) % studyDeck.length);
    }, [studyDeck.length, stopReading]);

    const goToPrev = useCallback(() => {
        if (studyDeck.length === 0) return;
        stopReading();
        setIsFlipped(false);
        setCurrentIndex(prev => (prev - 1 + studyDeck.length) % studyDeck.length);
    }, [studyDeck.length, stopReading]);

    const scrambleDeck = () => {
        stopReading();
        const newDeck = [...deck].sort(() => Math.random() - 0.5);
        setDeck(newDeck);
        setCurrentIndex(0);
        setIsFlipped(false);
        setConfidence({});
        trackEvent(Events.deck_scramble, { folderId: folder.id, size: newDeck.length });
    };

    const toggleFlag = (cardId) => {
        setDeck(prev => prev.map(c => c.id === cardId ? { ...c, isFlagged: !c.isFlagged } : c));
        trackEvent(Events.card_flag_toggle, { folderId: folder.id, cardId });
    };

    const handleReviewModeChange = (mode) => {
        stopReading();
        setReviewMode(mode);
        setCurrentIndex(0);
        setIsFlipped(false);
        setConfidence({});
        trackEvent(Events.review_mode_change, { folderId: folder.id, mode });
    };

    const handleCloseClick = async () => {
        const studiedCount = deck.filter(c => c.lastViewed).length;
        const flagCount = deck.filter(c => c.isFlagged).length;
        trackEvent(Events.study_end, { folderId: folder.id, studied: studiedCount, flagged: flagCount, total: deck.length });
        try {
            await recordStudySession({ folderId: folder.id, folderName: folder.name, studied: studiedCount, flagged: flagCount, total: deck.length, durationSec: null });
        } catch (e) {
            console.warn('recordStudySession failed', e);
        }
        onClose(deck);
    };

    const handleConfidence = (key) => {
        const updatedCard = applyRating(currentCard, key);
        setConfidence(prev => ({ ...prev, [currentCard.id]: key }));
        onCardRated?.(updatedCard);
        goToNext();
    };

    const toggleStar = (cardId) => {
        setDeck(prev => prev.map(c => c.id === cardId ? { ...c, isStarred: !c.isStarred } : c));
    };

    // Update handlersRef after all functions are defined — keyboard effect reads from this ref
    handlersRef.current = {
        goToNext, goToPrev, handleCardClick, handleConfidence,
        closeSelf: handleCloseClick,
        isFlipped, currentCard,
        toggleStarCurrent: () => { if (currentCard) toggleStar(currentCard.id); },
    };

    const resetForStudyAgain = () => {
        setConfidence({});
        setCurrentIndex(0);
        setIsFlipped(false);
    };

    const studyMissed = () => {
        const missedIds = new Set(
            studyDeck.filter(c => confidence[c.id] === 'again' || confidence[c.id] === 'hard').map(c => c.id)
        );
        setDeck(prev => prev.filter(c => missedIds.has(c.id)));
        setConfidence({});
        setCurrentIndex(0);
        setIsFlipped(false);
    };

    // Touch handlers
    const onTouchStart = (e) => {
        touchStartX.current = e.touches[0].clientX;
        touchStartY.current = e.touches[0].clientY;
    };
    const onTouchEnd = (e) => {
        const dx = e.changedTouches[0].clientX - touchStartX.current;
        const dy = e.changedTouches[0].clientY - touchStartY.current;
        if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return; // was a tap; let onClick handle it
        if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 40) {
            dx < 0 ? goToNext() : goToPrev();
        } else if (dy < -50 && Math.abs(dy) > Math.abs(dx)) {
            handleCardClick(); // swipe up = flip
        }
    };

    return (
        <>
        <div className="fixed inset-0 z-40 bg-slate-950/80 backdrop-blur-sm flex flex-col px-2 py-3 sm:px-4 sm:py-5">
            <div className="mx-auto w-full max-w-5xl rounded-2xl border border-white/10 bg-slate-900/95 shadow-2xl shadow-black/50 overflow-hidden flex flex-col" style={{ height: '100%', maxHeight: '100%' }}>

                {/* ── Header ─────────────────────────────────────────────── */}
                <div className="flex shrink-0 items-center justify-between px-4 py-3 border-b border-white/8">
                    <div className="flex items-center gap-3 min-w-0">
                        <h2 className="text-sm font-semibold text-white truncate">{folder.name}</h2>
                        <span className="shrink-0 text-[11px] font-medium text-emerald-300 bg-emerald-500/10 border border-emerald-500/20 px-2 py-0.5 rounded-full tabular-nums">
                            {knownCount}/{studyDeck.length} known
                        </span>
                    </div>
                    <button onClick={handleCloseClick} className="shrink-0 rounded-full p-2.5 sm:p-1.5 text-slate-400 hover:text-white hover:bg-white/10 transition">
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                </div>

                {/* ── Progress bar ───────────────────────────────────────── */}
                <div className="h-1 w-full shrink-0 bg-white/5">
                    <div
                        className="h-1 bg-gradient-to-r from-emerald-400 to-teal-400 transition-all duration-500"
                        style={{ width: `${progressPct}%` }}
                    />
                </div>

                {/* ── Toolbar ────────────────────────────────────────────── */}
                <div className="shrink-0 flex items-center gap-1.5 px-4 py-2.5 border-b border-white/8 overflow-x-auto no-scrollbar">
                    {/* Scramble */}
                    <button
                        onClick={scrambleDeck}
                        className="shrink-0 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium text-slate-300 bg-white/5 hover:bg-white/10 transition"
                    >
                        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                        Shuffle
                    </button>

                    {/* Arrange toggle */}
                    <button
                        onClick={() => { setIsArrangeMode(m => !m); setIsFlipped(false); }}
                        className={`shrink-0 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition ${isArrangeMode ? 'bg-teal-500/20 text-teal-200 ring-1 ring-teal-400/30' : 'text-slate-300 bg-white/5 hover:bg-white/10'}`}
                    >
                        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" /></svg>
                        {isArrangeMode ? 'Done' : 'Arrange'}
                    </button>

                    {/* Review mode pills */}
                    <div className="shrink-0 flex items-center gap-1 bg-white/5 rounded-full p-0.5">
                        <button
                            onClick={() => handleReviewModeChange('all')}
                            className={`rounded-full px-3 py-1 text-xs font-medium transition ${reviewMode === 'all' ? 'bg-teal-600 text-white shadow-sm' : 'text-slate-400 hover:text-white'}`}
                        >All</button>
                        <button
                            onClick={() => handleReviewModeChange('flagged')}
                            className={`rounded-full px-3 py-1 text-xs font-medium transition ${reviewMode === 'flagged' ? 'bg-amber-500 text-slate-900 shadow-sm' : 'text-slate-400 hover:text-white'}`}
                        >
                            Flagged {flaggedCount > 0 && `(${flaggedCount})`}
                        </button>
                        <button
                            onClick={() => handleReviewModeChange('starred')}
                            className={`rounded-full px-3 py-1 text-xs font-medium transition ${reviewMode === 'starred' ? 'bg-sky-500 text-white shadow-sm' : 'text-slate-400 hover:text-white'}`}
                        >
                            ✦ Starred {starredCount > 0 && `(${starredCount})`}
                        </button>
                    </div>

                    <div className="ml-auto shrink-0 flex items-center gap-1.5">
                        {/* TTS play/stop */}
                        <button
                            onClick={() => {
                                if (isReading) { trackEvent(Events.tts_stop, { folderId: folder.id }); stopReading(); }
                                else { trackEvent(Events.tts_start, { folderId: folder.id }); setIsReading(true); }
                            }}
                            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition ${isReading ? 'bg-teal-600 text-white' : 'bg-white/5 text-slate-300 hover:bg-white/10'}`}
                        >
                            {isReading ? (
                                <><svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 24 24"><rect x="6" y="6" width="4" height="12" rx="1"/><rect x="14" y="6" width="4" height="12" rx="1"/></svg>Stop</>
                            ) : (
                                <><svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>Audio</>
                            )}
                        </button>

                        {/* Tutor chat */}
                        <button
                            onClick={() => setShowChat(true)}
                            className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium bg-white/5 text-slate-300 hover:bg-white/10 transition"
                            title="Ask the Tutor"
                        >
                            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>
                            Tutor
                        </button>

                        {/* Settings gear */}
                        <button
                            onClick={() => setSettingsOpen(o => !o)}
                            className={`inline-flex items-center justify-center rounded-full p-2.5 sm:p-1.5 text-xs transition ${settingsOpen ? 'bg-white/15 text-white' : 'text-slate-400 hover:text-white hover:bg-white/10'}`}
                            title="Settings & Games"
                        >
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
                        </button>
                    </div>
                </div>

                {/* ── Settings drawer ────────────────────────────────────── */}
                {settingsOpen && (
                    <div className="shrink-0 grid sm:grid-cols-3 gap-4 px-4 py-4 border-b border-white/8 bg-slate-950/40">
                        {/* TTS settings */}
                        <div className="space-y-3">
                            <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">Audio Settings</p>
                            <div>
                                <label className="block text-xs text-slate-400 mb-1">Voice</label>
                                <select
                                    value={selectedVoice}
                                    onChange={e => setSelectedVoice(e.target.value)}
                                    disabled={isReading}
                                    className="w-full rounded-xl bg-slate-800/70 border border-white/10 px-3 py-2 text-sm text-slate-200 disabled:opacity-50"
                                >
                                    {voices.map(v => <option key={v.name} value={v.name}>{v.name} ({v.lang})</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs text-slate-400 mb-1">
                                    Flip delay <span className="text-teal-300">{speechDelay}s</span>
                                </label>
                                <input type="range" min="1" max="10" step="1" value={speechDelay}
                                    onChange={e => setSpeechDelay(Number(e.target.value))}
                                    disabled={isReading} className="w-full accent-teal-500" />
                            </div>
                            <div>
                                <label className="block text-xs text-slate-400 mb-1">
                                    Speed <span className="text-teal-300">{speechRate}x</span>
                                </label>
                                <input type="range" min="0.5" max="2" step="0.1" value={speechRate}
                                    onChange={e => setSpeechRate(Number(e.target.value))}
                                    disabled={isReading} className="w-full accent-teal-500" />
                            </div>
                        </div>

                        {/* Study Modes */}
                        <div className="space-y-2">
                            <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">Study Modes</p>
                            <button onClick={() => { setSettingsOpen(false); setShowLearn(true); }}
                                className="w-full inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-teal-600 to-emerald-600 hover:opacity-90 transition">
                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                                Learn Mode
                            </button>
                            <button onClick={() => { setSettingsOpen(false); setShowVoice(true); }}
                                className="w-full inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium text-slate-200 bg-slate-800/70 hover:bg-slate-800 ring-1 ring-white/10 transition">
                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" /></svg>
                                Voice Mode
                            </button>
                            {onLaunchExam && (
                                <button onClick={() => { setSettingsOpen(false); onLaunchExam(folder); }}
                                    className="w-full inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium text-slate-200 bg-slate-800/70 hover:bg-slate-800 ring-1 ring-white/10 transition">
                                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                                    Test Mode
                                </button>
                            )}
                            <button onClick={() => { setSettingsOpen(false); setShowShare(true); }}
                                className="w-full inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium text-slate-200 bg-slate-800/70 hover:bg-slate-800 ring-1 ring-white/10 transition">
                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" /></svg>
                                Share Set
                            </button>
                        </div>

                        {/* Games */}
                        <div className="space-y-2">
                            <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">Game Modes</p>
                            <button
                                onClick={() => {
                                    const gamesLeft = entitlements?.includedGamesRemaining ?? 0;
                                    if (isDevMode || gamesLeft > 0) { onLaunchGame(folder); return; }
                                    if (typeof onOpenCredits === 'function') onOpenCredits({ type: 'games', folder });
                                }}
                                className="w-full inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-amber-600 to-orange-600 hover:opacity-90 transition"
                            >
                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" /></svg>
                                Verbatim Master
                            </button>
                            <button
                                onClick={() => onLaunchAnamnesisNemesis(folder)}
                                className="w-full inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium text-slate-200 bg-slate-800/70 hover:bg-slate-800 ring-1 ring-white/10 transition"
                            >
                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                Anamnesis Nemesis
                            </button>
                        </div>
                    </div>
                )}

                {/* ── Main area ──────────────────────────────────────────── */}
                <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain" style={{ WebkitOverflowScrolling: 'touch' }}>

                    {/* Arrange mode */}
                    {isArrangeMode ? (
                        <div className="px-4 py-4 space-y-2">
                            <p className="text-xs text-slate-500 text-center pb-1">Drag cards to reorder</p>
                            {deck.map((card, index) => (
                                <div
                                    key={card.id}
                                    draggable
                                    onDragStart={e => e.dataTransfer.setData('cardIndex', String(index))}
                                    onDragOver={e => e.preventDefault()}
                                    onDrop={e => {
                                        const from = Number(e.dataTransfer.getData('cardIndex'));
                                        if (from === index) return;
                                        const next = [...deck];
                                        const [item] = next.splice(from, 1);
                                        next.splice(index, 0, item);
                                        setDeck(next);
                                    }}
                                    className="flex items-center gap-3 rounded-xl px-3 py-2.5 bg-slate-900/60 border border-white/8 cursor-move text-slate-200 hover:border-white/15 transition"
                                >
                                    <span className="shrink-0 text-slate-600">
                                        <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M8 6a2 2 0 110-4 2 2 0 010 4zM8 14a2 2 0 110-4 2 2 0 010 4zM8 22a2 2 0 110-4 2 2 0 010 4zM16 6a2 2 0 110-4 2 2 0 010 4zM16 14a2 2 0 110-4 2 2 0 010 4zM16 22a2 2 0 110-4 2 2 0 010 4z"/></svg>
                                    </span>
                                    <span className="text-xs text-slate-500 tabular-nums shrink-0">{index + 1}</span>
                                    <span className="text-sm truncate"><ContentRenderer content={card.question} /></span>
                                </div>
                            ))}
                        </div>

                    ) : studyDeck.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-16 text-center gap-2">
                            <p className="text-slate-300">No cards to display.</p>
                            {reviewMode === 'flagged' && (
                                <p className="text-sm text-slate-500">Flag cards during your session to study them here.</p>
                            )}
                        </div>

                    ) : allRated ? (
                        /* ── Completion screen ──────────────────────────── */
                        <div className="flex flex-col items-center justify-center py-12 px-4 text-center gap-5">
                            <div className="text-5xl">🎉</div>
                            <div>
                                <h3 className="text-xl font-bold text-white">Round Complete!</h3>
                                <p className="text-slate-400 text-sm mt-1">{knownCount} of {studyDeck.length} cards mastered</p>
                            </div>

                            {/* Confidence breakdown */}
                            <div className="flex flex-wrap items-center justify-center gap-2">
                                {CONFIDENCE_OPTIONS.map(({ key, label }) => {
                                    const count = studyDeck.filter(c => confidence[c.id] === key).length;
                                    return count > 0 ? (
                                        <span key={key} className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-sm font-medium ${CONFIDENCE_BADGE[key]}`}>
                                            {label} <span className="font-bold">{count}</span>
                                        </span>
                                    ) : null;
                                })}
                            </div>

                            {/* Actions */}
                            <div className="flex flex-wrap items-center justify-center gap-2 pt-2">
                                <button
                                    onClick={resetForStudyAgain}
                                    className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-teal-600 to-emerald-600 hover:opacity-90 transition shadow-lg shadow-teal-500/20"
                                >
                                    Study Again
                                </button>
                                {(againCount + hardCount) > 0 && (
                                    <button
                                        onClick={studyMissed}
                                        className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium text-white bg-rose-600/80 hover:bg-rose-600 transition"
                                    >
                                        Study Missed ({againCount + hardCount})
                                    </button>
                                )}
                                <button
                                    onClick={handleCloseClick}
                                    className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium text-slate-300 bg-white/5 hover:bg-white/10 ring-1 ring-white/10 transition"
                                >
                                    Done
                                </button>
                            </div>
                        </div>

                    ) : (
                        /* ── Flashcard ───────────────────────────────────── */
                        <div className="px-3 sm:px-6 py-4 flex flex-col items-center gap-4">
                            {/* Counter */}
                            <p className="text-xs text-slate-500 tabular-nums">
                                {currentIndex + 1} <span className="text-slate-700">/</span> {studyDeck.length}
                            </p>

                            {/* 3D Flip card */}
                            <div
                                className="w-full max-w-3xl cursor-pointer select-none"
                                style={{ perspective: '1200px' }}
                                onClick={handleCardClick}
                                onTouchStart={onTouchStart}
                                onTouchEnd={onTouchEnd}
                            >
                                <div className="min-h-[280px] sm:min-h-[380px]" style={{
                                    position: 'relative',
                                    transformStyle: 'preserve-3d',
                                    transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
                                    transition: 'transform 0.42s cubic-bezier(0.4, 0, 0.2, 1)',
                                }}>
                                    {/* Front face */}
                                    <div
                                        style={{ backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden' }}
                                        className="absolute inset-0 rounded-2xl border border-teal-500/25 bg-gradient-to-br from-slate-950 to-slate-900 ring-1 ring-teal-500/15 shadow-xl shadow-teal-500/10 p-5 flex flex-col"
                                    >
                                        <div className="flex items-start justify-between mb-3">
                                            <span className="text-[10px] font-semibold uppercase tracking-widest text-teal-400/70">Question</span>
                                            <div className="flex items-center gap-1">
                                                <button
                                                    onClick={e => { e.stopPropagation(); toggleStar(currentCard.id); }}
                                                    className={`rounded-full p-2.5 sm:p-1 transition ${currentCard?.isStarred ? 'text-sky-400' : 'text-slate-600 hover:text-sky-400'}`}
                                                    title="Star card (S)"
                                                >
                                                    <svg className="h-4 w-4" fill={currentCard?.isStarred ? 'currentColor' : 'none'} viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" /></svg>
                                                </button>
                                                <button
                                                    onClick={e => { e.stopPropagation(); toggleFlag(currentCard.id); }}
                                                    className={`rounded-full p-2.5 sm:p-1 transition ${currentCard?.isFlagged ? 'text-amber-400' : 'text-slate-600 hover:text-amber-400'}`}
                                                    title="Flag card"
                                                >
                                                    <svg className="h-4 w-4" fill={currentCard?.isFlagged ? 'currentColor' : 'none'} viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" /></svg>
                                                </button>
                                            </div>
                                        </div>
                                        <div className="flex-1 min-h-0 overflow-y-auto flex flex-col items-center justify-center gap-3">
                                            {currentCard?.imageUrl && (
                                                <CardImage
                                                    imageUrl={currentCard.imageUrl}
                                                    alt="Card attachment"
                                                    className="max-h-48 sm:max-h-64 max-w-full rounded-xl border border-white/10 object-contain"
                                                />
                                            )}
                                            <p className="text-slate-100 text-base sm:text-lg leading-relaxed">
                                                <ContentRenderer content={currentCard?.question} />
                                            </p>
                                        </div>
                                        <p className="text-center text-[11px] text-slate-600 mt-3">Tap or swipe up to reveal answer</p>
                                    </div>

                                    {/* Back face */}
                                    <div
                                        style={{ backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
                                        className="absolute inset-0 rounded-2xl border border-emerald-500/25 bg-gradient-to-br from-slate-950 to-slate-900/90 ring-1 ring-emerald-500/15 shadow-xl shadow-emerald-500/10 p-5 flex flex-col"
                                    >
                                        <div className="flex items-start justify-between mb-3">
                                            <span className="text-[10px] font-semibold uppercase tracking-widest text-emerald-400/70">Answer</span>
                                            <div className="flex items-center gap-1">
                                                <button
                                                    onClick={e => { e.stopPropagation(); toggleStar(currentCard.id); }}
                                                    className={`rounded-full p-2.5 sm:p-1 transition ${currentCard?.isStarred ? 'text-sky-400' : 'text-slate-600 hover:text-sky-400'}`}
                                                    title="Star card (S)"
                                                >
                                                    <svg className="h-4 w-4" fill={currentCard?.isStarred ? 'currentColor' : 'none'} viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" /></svg>
                                                </button>
                                                <button
                                                    onClick={e => { e.stopPropagation(); toggleFlag(currentCard.id); }}
                                                    className={`rounded-full p-2.5 sm:p-1 transition ${currentCard?.isFlagged ? 'text-amber-400' : 'text-slate-600 hover:text-amber-400'}`}
                                                    title="Flag card"
                                                >
                                                    <svg className="h-4 w-4" fill={currentCard?.isFlagged ? 'currentColor' : 'none'} viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" /></svg>
                                                </button>
                                            </div>
                                        </div>
                                        <div className="flex-1 min-h-0 overflow-y-auto flex items-center">
                                            <p className="text-slate-100 text-base sm:text-lg leading-relaxed">
                                                <ContentRenderer content={currentCard?.answer} reactionSummary={currentCard?.reactionSummary} />
                                            </p>
                                        </div>
                                        {/* AI Explain — shown after marking Again or Hard */}
                                        {isFlipped && currentCard && (confidence[currentCard.id] === 'again' || confidence[currentCard.id] === 'hard') && (
                                            <div onClick={e => e.stopPropagation()}>
                                                <ExplainButton card={currentCard} />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Confidence buttons — shown only when flipped */}
                            {isFlipped && currentCard && (
                                <div className="w-full flex items-center gap-2 justify-center pt-1">
                                    {CONFIDENCE_OPTIONS.map(({ key, label, color }) => (
                                        <button
                                            key={key}
                                            onClick={() => handleConfidence(key)}
                                            className={`flex-1 sm:flex-none rounded-full px-3 sm:px-4 py-3 sm:py-1.5 text-sm font-medium transition min-h-[44px] ${color}`}
                                        >
                                            {label}
                                        </button>
                                    ))}
                                </div>
                            )}

                            {/* Swipe hint (mobile only, before first flip) */}
                            {!isFlipped && currentIndex === 0 && (
                                <p className="text-[11px] text-slate-600 sm:hidden">← swipe to navigate · swipe up to flip →</p>
                            )}
                        </div>
                    )}
                </div>

                {/* ── Nav bar ─────────────────────────────────────────────── */}
                {!isArrangeMode && studyDeck.length > 0 && !allRated && (
                    <div className="shrink-0 flex items-center justify-between gap-3 px-4 py-3 border-t border-white/8">
                        <button
                            onClick={goToPrev}
                            className="min-h-[44px] inline-flex items-center gap-1.5 rounded-full px-5 py-2.5 text-sm font-medium text-slate-300 bg-white/5 hover:bg-white/10 ring-1 ring-white/8 transition"
                        >
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                            Prev
                        </button>
                        <span className="text-xs text-slate-500 tabular-nums">{currentIndex + 1} / {studyDeck.length}</span>
                        <button
                            onClick={goToNext}
                            className="min-h-[44px] inline-flex items-center gap-1.5 rounded-full px-5 py-2.5 text-sm font-medium text-slate-300 bg-white/5 hover:bg-white/10 ring-1 ring-white/8 transition"
                        >
                            Next
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                        </button>
                    </div>
                )}
            </div>
        </div>

        {/* Study mode overlays rendered outside the viewer card */}
        {showLearn && <LearnMode folder={{ ...folder, cards: deck }} onClose={() => setShowLearn(false)} />}
        {showVoice && <VoiceAnswerMode folder={{ ...folder, cards: deck }} onClose={() => setShowVoice(false)} />}
        {showChat && <FolderChatSidebar folder={{ ...folder, cards: deck }} onClose={() => setShowChat(false)} />}
        {showShare && <ShareFolderModal folder={folder} onClose={() => setShowShare(false)} />}
        </>
    );
};

export default FlashcardViewer;
